import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { RecentSearchUser } from '@/lib/types';
import { useCrossTabSync } from './useCrossTabSync';
import { normalizeError } from '@/lib/error-types';

export const useRecentSearches = (limit: number = 5) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { broadcastBatchInvalidation } = useCrossTabSync();

  // Fetch recent searches
  const { data: recentSearches = [], isLoading } = useQuery({
    queryKey: ['recent-searches', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase.rpc('get_recent_searches', {
        limit_count: limit
      });

      if (error) throw normalizeError(error);
      return (data || []) as RecentSearchUser[];
    },
    enabled: !!user,
    staleTime: 60000, // Cache for 1 minute (searches don't change often)
  });

  // Add search to history
  const addSearchMutation = useMutation({
    mutationFn: async (searchedUserId: string) => {
      const { error } = await supabase.rpc('add_search_history', {
        searched_user_id_param: searchedUserId
      });
      
      if (error) throw normalizeError(error);
    },
    onSuccess: () => {
      // Invalidate recent searches to refresh the list
      queryClient.invalidateQueries({ queryKey: ['recent-searches'] });
      
      // Broadcast to other tabs
      broadcastBatchInvalidation([
        ['recent-searches']
      ], { mutationType: 'search-add' });
    },
  });

  // Clear search history
  const clearHistoryMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('clear_search_history');
      
      if (error) throw normalizeError(error);
      return data;
    },
    onSuccess: () => {
      // Invalidate recent searches to clear the list
      queryClient.invalidateQueries({ queryKey: ['recent-searches'] });
      
      // Broadcast to other tabs
      broadcastBatchInvalidation([
        ['recent-searches']
      ], { mutationType: 'search-clear' });
    },
  });

  return {
    recentSearches,
    isLoading,
    addSearch: addSearchMutation.mutate,
    clearHistory: clearHistoryMutation.mutate,
    isAddingSearch: addSearchMutation.isPending,
    isClearingHistory: clearHistoryMutation.isPending,
  };
};
